import { useAuth, useUser } from "@clerk/clerk-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

type Patient = {
	id: string;
	code: string;
	name: string | null;
	provider: string | null;
	site: string | null;
	createdAt: string;
};

export default function PatientSelectionPage() {
	const [patientCodeInput, setPatientCodeInput] = useState("");
	const [patientName, setPatientName] = useState("");
	const [provider, setProvider] = useState("");
	const [site, setSite] = useState("");
	const [patients, setPatients] = useState<Patient[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState("");
	const [selectedPatientId, setSelectedPatientId] = useState<string | null>(
		() => sessionStorage.getItem("expandedPatientId"),
	);
	const [assessments, setAssessments] = useState<
		{ id: string; createdAt: string }[]
	>([]);
	const [loadingAssessments, setLoadingAssessments] = useState(false);
	const [showCreateForm, setShowCreateForm] = useState(false);
	const [searchQuery, setSearchQuery] = useState("");
	const navigate = useNavigate();
	const { user, isLoaded } = useUser();
	const { getToken } = useAuth();

	// Fetch patients on mount
	useEffect(() => {
		if (isLoaded && user) {
			fetchPatients();
		}
	}, [isLoaded, user]);

	// Persist expanded patient ID to sessionStorage
	useEffect(() => {
		if (selectedPatientId) {
			sessionStorage.setItem("expandedPatientId", selectedPatientId);
		} else {
			sessionStorage.removeItem("expandedPatientId");
		}
	}, [selectedPatientId]);

	// Auto-fetch assessments for restored patient on mount
	useEffect(() => {
		if (selectedPatientId && isLoaded && user) {
			fetchAssessments(selectedPatientId);
		}
	}, [isLoaded, user]);

	async function fetchPatients() {
		try {
			setIsLoading(true);
			const token = await getToken();
			const res = await fetch(`${import.meta.env.VITE_BASE_API_URL}/patients`, {
				headers: {
					Authorization: `Bearer ${token}`,
				},
			});

			if (!res.ok) throw new Error("Failed to fetch patients");

			const data = await res.json();
			setPatients(data);
		} catch (err: any) {
			setError(err.message);
		} finally {
			setIsLoading(false);
		}
	}

	async function handleCreate(e: React.FormEvent) {
		e.preventDefault();
		setError("");

		if (!patientCodeInput.trim()) {
			setError("Patient code is required");
			return;
		}

		try {
			const token = await getToken();
			const res = await fetch(`${import.meta.env.VITE_BASE_API_URL}/patients`, {
				method: "POST",
				headers: {
					Authorization: `Bearer ${token}`,
					"Content-Type": "application/json",
				},
				body: JSON.stringify({
					code: patientCodeInput.trim(),
					name: patientName.trim() || null,
					provider: provider || null,
					site: site || null,
				}),
			});

			if (!res.ok) {
				const data = await res.json();
				throw new Error(data.error || "Failed to create patient");
			}

			// Reset form and refresh list
			setPatientCodeInput("");
			setPatientName("");
			setProvider("");
			setSite("");
			setShowCreateForm(false);
			fetchPatients();
		} catch (err: any) {
			setError(err.message);
		}
	}

	function handleSelectPatient(patient: Patient) {
		// Store selected patient in localStorage for session page
		localStorage.setItem("selectedPatient", JSON.stringify(patient));
		navigate("/session");
	}

	async function fetchAssessments(patientId: string) {
		setLoadingAssessments(true);
		try {
			const token = await getToken();
			const res = await fetch(
				`${import.meta.env.VITE_BASE_API_URL}/assessments?patientId=${patientId}`,
				{ headers: { Authorization: `Bearer ${token}` } },
			);
			if (!res.ok) throw new Error("Failed to fetch assessments");
			const data = await res.json();
			setAssessments(data);
		} catch {
			setAssessments([]);
		} finally {
			setLoadingAssessments(false);
		}
	}

	async function handleExportAll(patient: Patient) {
		try {
			const token = await getToken();

			// Fetch all assessment IDs first
			const listRes = await fetch(
				`${import.meta.env.VITE_BASE_API_URL}/assessments?patientId=${patient.id}`,
				{ headers: { Authorization: `Bearer ${token}` } },
			);
			if (!listRes.ok) throw new Error("Failed to fetch assessments");
			const list = await listRes.json();

			if (list.length === 0) return;

			// Fetch full data for each assessment
			const fullAssessments = await Promise.all(
				list.map(async (a: { id: string }) => {
					const res = await fetch(
						`${import.meta.env.VITE_BASE_API_URL}/assessments/${a.id}`,
						{ headers: { Authorization: `Bearer ${token}` } },
					);
					if (!res.ok) throw new Error("Failed to fetch assessment");
					return res.json();
				}),
			);

			// Generate CSV
			const rows: string[] = ["patientCode,version_date,eye,tool,param,value"];

			// Escape CSV values that contain commas, quotes, or newlines
			const esc = (v: string | number | boolean): string => {
				const s = String(v);
				if (s.includes(",") || s.includes('"') || s.includes("\n")) {
					return `"${s.replace(/"/g, '""')}"`;
				}
				return s;
			};

			for (const assessment of fullAssessments) {
				const versionDate = assessment.createdAt;
				for (const eyeKey of ["left", "right"] as const) {
					const eyeState = assessment.eyeStates?.[eyeKey];
					if (!eyeState) continue;

					const push = (
						tool: string,
						param: string,
						value: string | number | boolean,
					) => {
						rows.push(
							[patient.code, versionDate, eyeKey, tool, param, value]
								.map(esc)
								.join(","),
						);
					};

					// Blurriness
					if (eyeState.blurriness?.sigma > 0) {
						push("blurriness", "sigma", eyeState.blurriness.sigma);
					}

					// Contrast
					if (
						eyeState.contrast?.level !== undefined &&
						eyeState.contrast.level !== 1
					) {
						push("contrast", "level", eyeState.contrast.level);
						if (eyeState.contrast.preset)
							push("contrast", "preset", eyeState.contrast.preset);
						if (eyeState.contrast.mode)
							push("contrast", "mode", eyeState.contrast.mode);
					}

					// Image Brightness
					if (
						eyeState.imageBrightness?.value !== undefined &&
						eyeState.imageBrightness.value !== 1
					) {
						push("imageBrightness", "value", eyeState.imageBrightness.value);
					}

					// Colour Shift
					if (
						eyeState.colourShift &&
						(eyeState.colourShift.hue !== 0 ||
							eyeState.colourShift.saturation !== 1)
					) {
						push("colourShift", "hue", eyeState.colourShift.hue);
						push("colourShift", "saturation", eyeState.colourShift.saturation);
						if (eyeState.colourShift.preset)
							push("colourShift", "preset", eyeState.colourShift.preset);
					}

					// Rotation
					if (eyeState.rotation?.angle !== 0) {
						push("rotation", "angle", eyeState.rotation.angle);
					}

					// Diplopia
					if (eyeState.diplopia?.enabled) {
						push("diplopia", "enabled", true);
						if (eyeState.diplopia.offset) {
							push("diplopia", "offset_x", eyeState.diplopia.offset.x);
							push("diplopia", "offset_y", eyeState.diplopia.offset.y);
						}
						if (eyeState.diplopia.overlap)
							push("diplopia", "overlap", eyeState.diplopia.overlap);
					}

					// Metamorphopsia
					if (eyeState.metamorphopsia?.enabled) {
						push("metamorphopsia", "enabled", true);
						push(
							"metamorphopsia",
							"amplitude",
							eyeState.metamorphopsia.amplitude,
						);
						push(
							"metamorphopsia",
							"frequency",
							eyeState.metamorphopsia.frequency,
						);
						push(
							"metamorphopsia",
							"direction",
							eyeState.metamorphopsia.direction,
						);
					}

					// Anisoconia
					if (eyeState.anisoconia?.headScale !== 1) {
						push("anisoconia", "headScale", eyeState.anisoconia.headScale);
					}

					// Scotoma
					if (eyeState.scotoma?.length > 0) {
						eyeState.scotoma.forEach(
							(
								spot: {
									x: number;
									y: number;
									radius: number;
									intensity: number;
								},
								i: number,
							) => {
								push("scotoma", `spot${i + 1}_x`, spot.x);
								push("scotoma", `spot${i + 1}_y`, spot.y);
								push(
									"scotoma",
									`spot${i + 1}_area`,
									Math.round(Math.PI * spot.radius ** 2),
								);
								push("scotoma", `spot${i + 1}_opacity`, spot.intensity);
							},
						);
					}

					// Distortion
					if (eyeState.distortion?.points?.length > 0) {
						eyeState.distortion.points.forEach(
							(
								p: {
									original: { x: number; y: number };
									current: { x: number; y: number };
								},
								i: number,
							) => {
								push("distortion", `point${i + 1}_original_x`, p.original.x);
								push("distortion", `point${i + 1}_original_y`, p.original.y);
								push("distortion", `point${i + 1}_current_x`, p.current.x);
								push("distortion", `point${i + 1}_current_y`, p.current.y);
							},
						);
						push("distortion", "power", eyeState.distortion.power);
						push("distortion", "range", eyeState.distortion.range);
					}

					// Vacuum
					if (eyeState.vacuum?.points?.length > 0) {
						eyeState.vacuum.points.forEach(
							(
								p: {
									original: { x: number; y: number };
									current: { x: number; y: number };
								},
								i: number,
							) => {
								push("vacuum", `point${i + 1}_original_x`, p.original.x);
								push("vacuum", `point${i + 1}_original_y`, p.original.y);
								push("vacuum", `point${i + 1}_current_x`, p.current.x);
								push("vacuum", `point${i + 1}_current_y`, p.current.y);
							},
						);
						push("vacuum", "power", eyeState.vacuum.power);
						push("vacuum", "range", eyeState.vacuum.range);
					}
				}
			}

			// Download CSV
			const csv = rows.join("\n");
			const blob = new Blob([csv], { type: "text/csv" });
			const url = URL.createObjectURL(blob);
			const a = document.createElement("a");
			a.href = url;
			a.download = `${patient.code}_all_assessments.csv`;
			document.body.appendChild(a);
			a.click();
			document.body.removeChild(a);
			URL.revokeObjectURL(url);
		} catch (err: unknown) {
			const message = err instanceof Error ? err.message : "Failed to export";
			setError(message);
		}
	}

	const filteredPatients = patients.filter((p) => {
		if (!searchQuery.trim()) return true;
		const q = searchQuery.toLowerCase();
		return (
			p.code.toLowerCase().includes(q) || p.name?.toLowerCase().includes(q)
		);
	});

	// Auth guard
	if (!isLoaded) {
		return (
			<div className="min-h-screen flex items-center justify-center">
				<div>Loading...</div>
			</div>
		);
	}

	if (!user) {
		navigate("/login");
		return null;
	}

	return (
		<div className="min-h-screen bg-background p-6">
			<div className="max-w-6xl mx-auto">
				{/* Header row: title, search, add button */}
				<div className="flex items-center gap-4 mb-4">
					<h1 className="text-xl font-bold text-foreground shrink-0">
						Patients
					</h1>
					<input
						type="text"
						value={searchQuery}
						onChange={(e) => setSearchQuery(e.target.value)}
						placeholder="Search by code or name..."
						className="flex-1 px-3 py-2 bg-card border border-border rounded-lg text-sm text-foreground placeholder:text-dim focus:outline-none focus:ring-2 focus:ring-accent-indigo"
					/>
					<button
						type="button"
						onClick={() => setShowCreateForm((prev) => !prev)}
						className={`shrink-0 px-4 py-2 rounded-lg text-sm font-semibold transition ${
							showCreateForm
								? "bg-muted text-body-3"
								: "bg-accent-indigo text-white hover:bg-accent-indigo/90"
						}`}
					>
						{showCreateForm ? "Cancel" : "+ Add Patient"}
					</button>
				</div>

				{/* Compact create form — horizontal layout */}
				{showCreateForm && (
					<form
						onSubmit={handleCreate}
						className="bg-card border border-border rounded-lg p-4 mb-4"
					>
						<div className="grid grid-cols-[1fr_1fr_auto_auto_auto] gap-3 items-end">
							<div>
								<label className="block text-xs font-medium text-body-3 mb-1">
									Code *
								</label>
								<input
									type="text"
									value={patientCodeInput}
									onChange={(e) => setPatientCodeInput(e.target.value)}
									placeholder="P001"
									className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-foreground placeholder:text-dim focus:outline-none focus:ring-2 focus:ring-accent-indigo"
									required
								/>
							</div>
							<div>
								<label className="block text-xs font-medium text-body-3 mb-1">
									Name
								</label>
								<input
									type="text"
									value={patientName}
									onChange={(e) => setPatientName(e.target.value)}
									placeholder="Optional"
									className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-foreground placeholder:text-dim focus:outline-none focus:ring-2 focus:ring-accent-indigo"
								/>
							</div>
							<div>
								<label className="block text-xs font-medium text-body-3 mb-1">
									Provider *
								</label>
								<select
									value={provider}
									onChange={(e) => setProvider(e.target.value)}
									className="px-3 py-2 bg-background border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-accent-indigo"
									required
								>
									<option value="">Select...</option>
									<option value="Dr. Rajeev Muni">Dr. Rajeev Muni</option>
									<option value="Dr. Radha Kohly">Dr. Radha Kohly</option>
								</select>
							</div>
							<div>
								<label className="block text-xs font-medium text-body-3 mb-1">
									Site *
								</label>
								<select
									value={site}
									onChange={(e) => setSite(e.target.value)}
									className="px-3 py-2 bg-background border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-accent-indigo"
									required
								>
									<option value="">Select...</option>
									<option value="RCT">RCT</option>
									<option value="St. Michael's">St. Michael&apos;s</option>
								</select>
							</div>
							<button
								type="submit"
								className="px-5 py-2 bg-accent-indigo text-white rounded-lg text-sm font-semibold hover:bg-accent-indigo/90 transition focus:outline-none focus:ring-2 focus:ring-accent-indigo"
							>
								Create
							</button>
						</div>
						{error && (
							<div className="mt-3 bg-destructive/10 border border-destructive/20 text-destructive px-4 py-2 rounded-lg text-sm">
								{error}
							</div>
						)}
					</form>
				)}

				{/* Error banner (when form is hidden) */}
				{error && !showCreateForm && (
					<div className="mb-4 bg-destructive/10 border border-destructive/20 text-destructive px-4 py-2 rounded-lg text-sm">
						{error}
					</div>
				)}

				{/* Patient grid */}
				{isLoading ? (
					<div className="text-center py-12 text-dim">Loading patients...</div>
				) : filteredPatients.length === 0 ? (
					<div className="text-center py-12 text-dim">
						{searchQuery.trim()
							? "No patients match your search."
							: "No patients yet. Add one above to get started."}
					</div>
				) : (
					<div className="grid grid-cols-2 gap-3">
						{filteredPatients.map((patient) => {
							const isSelected = selectedPatientId === patient.id;
							return (
								<div
									key={patient.id}
									className={`bg-card border rounded-lg transition ${
										isSelected
											? "border-accent-indigo/40 ring-1 ring-accent-indigo/20"
											: "border-border hover:border-accent-indigo/30 hover:bg-muted"
									}`}
								>
									<button
										type="button"
										onClick={() => {
											if (isSelected) {
												setSelectedPatientId(null);
											} else {
												setSelectedPatientId(patient.id);
												fetchAssessments(patient.id);
											}
										}}
										className="w-full text-left px-4 py-3"
									>
										<div className="flex items-baseline gap-2">
											<span className="font-semibold text-foreground">
												{patient.code}
											</span>
											{patient.name && (
												<span className="text-sm text-body-3">
													{patient.name}
												</span>
											)}
										</div>
										{(patient.provider || patient.site) && (
											<div className="text-xs text-dim mt-0.5">
												{[patient.provider, patient.site]
													.filter(Boolean)
													.join(" \u00B7 ")}
											</div>
										)}
										<div className="text-xs text-dim mt-1">
											{new Date(patient.createdAt).toLocaleDateString()}
										</div>
									</button>

									{/* Expanded assessment panel */}
									{isSelected && (
										<div className="px-4 pb-3 pt-2 border-t border-border">
											{loadingAssessments ? (
												<div className="text-sm text-dim py-1">
													Loading history...
												</div>
											) : (
												<>
													<div className="flex items-center justify-between mb-2">
														<span className="text-xs font-medium text-body-2">
															Assessments ({assessments.length})
														</span>
														{assessments.length > 0 && (
															<button
																type="button"
																onClick={(e) => {
																	e.stopPropagation();
																	handleExportAll(patient);
																}}
																className="text-xs text-accent-indigo hover:underline"
															>
																Export All
															</button>
														)}
													</div>
													{assessments.length === 0 ? (
														<div className="text-xs text-dim mb-2">
															No saved assessments yet.
														</div>
													) : (
														<div className="space-y-1 mb-2">
															{assessments.map((a) => (
																<div
																	key={a.id}
																	className="flex items-center justify-between text-xs py-0.5"
																>
																	<span className="text-body-3">
																		{new Date(a.createdAt).toLocaleString()}
																	</span>
																	<button
																		type="button"
																		onClick={(e) => {
																			e.stopPropagation();
																			localStorage.setItem(
																				"selectedPatient",
																				JSON.stringify(patient),
																			);
																			navigate(`/session?assessmentId=${a.id}`);
																		}}
																		className="text-accent-indigo hover:underline font-medium"
																	>
																		View
																	</button>
																</div>
															))}
														</div>
													)}
													<button
														type="button"
														onClick={(e) => {
															e.stopPropagation();
															handleSelectPatient(patient);
														}}
														className="w-full text-center py-1.5 bg-accent-indigo/10 text-accent-indigo rounded-lg text-sm font-medium hover:bg-accent-indigo/15 transition"
													>
														+ New Assessment
													</button>
												</>
											)}
										</div>
									)}
								</div>
							);
						})}
					</div>
				)}
			</div>
			<div className="text-center py-6 text-xs text-dim">
				Powered by{" "}
				<a
					href="https://getqatalyst.com"
					target="_blank"
					rel="noopener noreferrer"
					className="text-accent-indigo hover:underline"
				>
					Qatalyst Labs
				</a>
			</div>
		</div>
	);
}
